import React, { useState, useEffect } from 'react';
import { Icons } from './Icons';
import { User, UserRole } from '../types';

interface UserManagementProps {
    authHeaders: any;
    onViewUserServers: (userId: string) => void;
}

export const UserManagement: React.FC<UserManagementProps> = ({ authHeaders, onViewUserServers }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newUser, setNewUser] = useState({ username: '', password: '', role: 'user' as UserRole });
  const [error, setError] = useState('');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ 
          action: 'list_users',
          auth_role: authHeaders.auth_role // Rol kontrolü
      });
      const res = await fetch(`/api/backend.php?${params.toString()}`);
      const data = await res.json();
      if (data.success) {
        setUsers(data.users);
      }
    } catch (e) { console.error("API Hatası", e); } 
    finally { setLoading(false); }
  };

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      const res = await fetch('/api/backend.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'add_user', ...newUser, ...authHeaders })
      });
      const data = await res.json();
      if (data.success) {
        setShowAddModal(false);
        setNewUser({ username: '', password: '', role: 'user' });
        fetchUsers();
      } else { setError(data.message); }
    } catch (e) { setError('Bağlantı hatası.'); }
  };

  const handleDeleteUser = async (id: string) => {
    if(!confirm('Bu kullanıcıyı silmek istediğinize emin misiniz?')) return;
    try {
        await fetch('/api/backend.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ action: 'delete_user', user_id: id, ...authHeaders })
        });
        fetchUsers();
    } catch(e) { alert('Silme hatası.'); }
  };

  return (
    <div className="p-8">
      <header className="mb-8 flex items-center justify-between">
        <div>
           <h1 className="text-3xl font-bold mb-2 flex items-center gap-3">
             <div className="p-2 bg-blue-600/20 rounded-lg"><Icons.Users className="w-8 h-8 text-blue-500" /></div>
             Kullanıcı Yönetimi
           </h1>
        </div>
        <button onClick={() => setShowAddModal(true)} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors">
          <Icons.UserPlus className="w-5 h-5" /> Yeni Kullanıcı
        </button>
      </header>

      <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden shadow-xl">
        <table className="w-full text-left">
            <thead className="bg-gray-800/50 text-gray-400 text-sm uppercase font-medium">
                <tr>
                    <th className="p-5">ID</th>
                    <th className="p-5">Kullanıcı Adı</th>
                    <th className="p-5">Rol</th>
                    <th className="p-5 text-right">İşlemler</th>
                </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
                {users.map(user => (
                    <tr key={user.id} className="hover:bg-gray-800/30 transition-colors">
                        <td className="p-5 text-gray-500 font-mono">#{user.id}</td>
                        <td className="p-5 font-medium text-white">{user.username}</td>
                        <td className="p-5">
                            <span className={`px-2 py-1 rounded text-xs font-bold uppercase ${user.role === 'admin' ? 'bg-purple-900/30 text-purple-400 border border-purple-800' : 'bg-gray-800 text-gray-400 border border-gray-700'}`}>
                                {user.role}
                            </span>
                        </td>
                        <td className="p-5 text-right flex justify-end gap-2">
                            <button onClick={() => onViewUserServers(user.id)} className="p-2 text-blue-400 hover:bg-blue-900/20 rounded transition-colors" title="Sunucularını Yönet">
                                <Icons.Server className="w-5 h-5" />
                            </button>
                            {user.role !== 'admin' || user.id !== '1' ? (
                                <button onClick={() => handleDeleteUser(user.id)} className="p-2 text-gray-500 hover:text-red-400 hover:bg-red-900/10 rounded transition-colors" title="Sil">
                                    <Icons.UserX className="w-5 h-5" />
                                </button>
                            ) : null}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
      </div>

      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <div className="bg-gray-900 rounded-2xl border border-gray-800 shadow-2xl w-full max-w-md p-6">
                <h3 className="text-xl font-bold mb-4">Kullanıcı Ekle</h3>
                {error && <div className="mb-4 p-3 bg-red-900/20 text-red-400 text-sm rounded">{error}</div>}
                <form onSubmit={handleAddUser} className="space-y-4">
                    <div><label className="block text-sm text-gray-400 mb-1">Kullanıcı Adı</label><input required className="w-full bg-gray-950 border border-gray-800 rounded p-2 text-white" value={newUser.username} onChange={e => setNewUser({...newUser, username: e.target.value})} /></div>
                    <div><label className="block text-sm text-gray-400 mb-1">Şifre</label><input required type="password" className="w-full bg-gray-950 border border-gray-800 rounded p-2 text-white" value={newUser.password} onChange={e => setNewUser({...newUser, password: e.target.value})} /></div>
                    <div>
                        <label className="block text-sm text-gray-400 mb-1">Rol</label>
                        <select className="w-full bg-gray-950 border border-gray-800 rounded p-2 text-white" value={newUser.role} onChange={e => setNewUser({...newUser, role: e.target.value as UserRole})}>
                            <option value="user">User</option>
                            <option value="admin">Admin</option>
                        </select>
                    </div>
                    <div className="flex justify-end gap-2 mt-4">
                        <button type="button" onClick={() => setShowAddModal(false)} className="px-4 py-2 text-gray-400 hover:text-white">İptal</button>
                        <button type="submit" className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded">Ekle</button>
                    </div>
                </form>
            </div>
        </div>
      )}
    </div>
  );
};